import { compose, without } from 'ramda'

import {
  CARRY,
  CARRY_TYPES,
  SIMPLE,
  REFERENCED,
  ACTIVE_LOW,
  DIGITAL_INPUT,
  ANALOG_INPUT,
  CAN_INPUT,
  VOLT_0_6,
  NATIVE_INPUT,
  SWITCH_INPUT,
} from 'utils/constants'
import strings from 'utils/strings'
import { set, setIn, removeIn, removeAllIn } from 'utils/immutable'
import { isCanInput, isDigitalInput } from 'utils/types/features'
import { DEFAULT_NUM_BITS } from 'constants/conditions'
import * as actions from 'actions/feature/inputs'

// Map subType to description for single conditions
const SINGLE_CONDITION_DESCRIPTIONS = {
  [DIGITAL_INPUT]: strings.digital_input_pin,
  [ANALOG_INPUT]: strings.analog_input_pin,
  [NATIVE_INPUT]: strings.native_input,
  [CAN_INPUT]: strings.can_input,
  [SWITCH_INPUT]: strings.switch_input,
}

export default {
  [actions.UPDATE_INPUT_CONFIGS]: updateInputConfigs,
  [actions.SWITCH_INPUT_TYPE]: switchInputType,
  [actions.UPDATE_SELECTED_INPUT]: updateSelectedInput,
  [actions.SIGNALS_RECEIVED]: updateSignals,
  [actions.REQUESTING_SIGNALS]: setRequestingSignals,
  [actions.PINS_RECEIVED]: updateOutputPins,
  [actions.SUBGROUPS_RECEIVED]: updateSubGroups,
}

function updateInputConfigs(state, { conditionUris, inputConfig }) {
  let nextState = state
  if (conditionUris) {
    conditionUris.forEach((queryUri) => {
      nextState = setIn(
        [...pathToConditions, queryUri, 'inputConfig'],
        inputConfig
      )(nextState)
    })
  }
  return nextState
}

function switchInputType(
  state,
  { queryUri, queryType, subType, options = {} }
) {
  if (!queryUri) {
    return state
  }
  const pathToCondition = [...pathToConditions, queryUri] // note: this is a condition, not query

  const setSubType = setIn([...pathToCondition, 'subType'], subType)
  const setNullSignal = setIn([...pathToCondition, 'signal'], null)
  const clearCustomDescription = removeIn([
    ...pathToCondition,
    'customDescription',
  ])
  // Update description based on the new subType for single conditions
  const newDescription = SINGLE_CONDITION_DESCRIPTIONS[subType]
  const setDescription = newDescription
    ? setIn([...pathToCondition, 'description'], newDescription)
    : (s) => s

  const removeCanFields = compose(
    removeIn([...pathToCondition, 'startBit']),
    removeIn([...pathToCondition, 'numberOfBits']),
    removeIn([...pathToCondition, 'canId'])
  )

  const removeNonCanFields = compose(
    removeIn([...pathToCondition, 'signal']),
    removeIn([...pathToCondition, 'refQueryUri']),
    removeIn([...pathToCondition, 'inputConfig']),
    removeIn([...pathToCondition, 'floatingCircuit'])
  )

  const newQueryType = CARRY_TYPES.has(queryType) ? queryType : SIMPLE

  if (isCanInput(subType)) {
    return compose(
      setIn([...pathToCondition, 'queryType'], CAN_INPUT),
      setIn(
        [...pathToCondition, 'numberOfBits'],
        options.defaultNumberOfBits || DEFAULT_NUM_BITS.SUB_CONDITION
      ),
      setSubType,
      setDescription,
      clearCustomDescription,
      removeNonCanFields
    )(state)
  }

  if (isDigitalInput(subType)) {
    return compose(
      setIn([...pathToCondition, 'queryType'], newQueryType),
      setIn(
        [...pathToCondition, 'inputConfig'],
        options.inputConfig || ACTIVE_LOW
      ),
      removeIn([...pathToCondition, 'floatingCircuit']),
      setSubType,
      setDescription,
      setNullSignal,
      clearCustomDescription,
      removeCanFields
    )(state)
  }

  const setCarryResult =
    subType === CARRY ? setIn([...pathToCondition, 'result'], true) : (s) => s

  return compose(
    setCarryResult,
    setIn([...pathToCondition, 'queryType'], newQueryType),
    removeIn([...pathToCondition, 'inputConfig']),
    removeIn([...pathToCondition, 'floatingCircuit']),
    setSubType,
    setDescription,
    setNullSignal,
    clearCustomDescription,
    removeCanFields
  )(state)
}

const getQueryType = (inputUriOrFloatingCircuit) => {
  const isCarry = inputUriOrFloatingCircuit.includes('queries')

  if (isCarry) return REFERENCED

  return SIMPLE
}

const getInputFieldPropName = (inputUriOrFloatingCircuit) => {
  const isSignal = inputUriOrFloatingCircuit.includes('signals')
  const isCarry = inputUriOrFloatingCircuit.includes('queries')

  if (isCarry) return 'refQueryUri'
  if (isSignal) return 'signal'

  return 'floatingCircuit'
}

const getInputConfig = (
  configuredInputs,
  defaultInputConfigurations,
  inputUriOrFloatingCircuit,
  subType,
  updatedField,
  currentInputConfig
) => {
  if (updatedField === 'floatingCircuit') {
    if (subType === DIGITAL_INPUT) {
      // Preserve current inputConfig for digital inputs when switching to floating circuit
      // Only use ACTIVE_LOW as fallback if no current config exists
      return currentInputConfig || ACTIVE_LOW
    }

    if (subType === ANALOG_INPUT) {
      return VOLT_0_6
    }
  }

  // Check if this signal is already configured in other conditions
  // This ensures all instances of the same DI pin have consistent polarity
  const updatedSignalConfig = configuredInputs[inputUriOrFloatingCircuit] || {}
  const { inputConfig: existingConfig } = updatedSignalConfig

  // For digital inputs, prioritize existing polarity from other conditions using the same signal
  // This ensures consistency when adding new conditions with the same DI pin
  if (subType === DIGITAL_INPUT) {
    if (existingConfig) {
      return existingConfig
    }
    // Prioritize the signal's own default config over any inherited currentInputConfig.
    // currentInputConfig may belong to a different pin (e.g., ACTIVE_LOW from SSAM_161)
    // that is not valid for the newly selected pin (e.g., SSAM_160 only supports ACTIVE_HIGH).
    return (
      defaultInputConfigurations[inputUriOrFloatingCircuit] ||
      currentInputConfig ||
      ACTIVE_LOW
    )
  }

  // For analog inputs, preserve current config when switching pins
  // This maintains the user's Measure selection (e.g., Voltage 0.0 - 18.5V)
  if (subType === ANALOG_INPUT) {
    if (existingConfig) {
      return existingConfig
    }
    // Fall back to current config or default
    return (
      currentInputConfig ||
      defaultInputConfigurations[inputUriOrFloatingCircuit] ||
      VOLT_0_6
    )
  }

  if (!existingConfig) {
    return defaultInputConfigurations[inputUriOrFloatingCircuit]
  }

  return existingConfig
}

const inputFieldNames = ['signal', 'refQueryUri', 'floatingCircuit']
function updateSelectedInput(
  state,
  {
    uri,
    subType,
    configuredInputs,
    defaultInputConfigs,
    inputUriOrFloatingCircuit,
  }
) {
  const queryType = getQueryType(inputUriOrFloatingCircuit)
  const updatedField = getInputFieldPropName(inputUriOrFloatingCircuit)

  // Get current inputConfig from state to preserve user's polarity selection
  const currentInputConfig = state.getIn([
    ...pathToConditions,
    uri,
    'inputConfig',
  ])

  const updatedInputConfig = getInputConfig(
    configuredInputs,
    defaultInputConfigs,
    inputUriOrFloatingCircuit,
    subType,
    updatedField,
    currentInputConfig
  )

  const fieldsToRemove = without([updatedField], inputFieldNames)

  const newState = compose(
    setIn([...pathToConditions, uri, updatedField], inputUriOrFloatingCircuit),
    removeAllIn(fieldsToRemove, [...pathToConditions, uri]),
    setIn([...pathToConditions, uri, 'queryType'], queryType),
    setIn([...pathToConditions, uri, 'inputConfig'], updatedInputConfig),
    setIn([...pathToConditions, uri, 'showHelp'], true),
    removeIn([...pathToConditions, uri, 'customDescription'])
  )(state)
  if (updatedField === 'refQueryUri' && queryType === REFERENCED) {
    return setIn([...pathToConditions, uri, 'operator'], 2, newState)
  }
  return newState
}

function updateSignals(state, { signals }) {
  return compose(
    set('isRequestingSignals', false),
    set('signals', signals)
  )(state)
}

function setRequestingSignals(state) {
  return set('isRequestingSignals', true)(state)
}

function updateOutputPins(state, { outputPins }) {
  return set('outputPins', outputPins)(state)
}

function updateSubGroups(state, { data }) {
  return set('subGroups', data)(state)
}

const pathToConditions = ['currentFeature', 'conditions']
